import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import metricCardStatusIcon from '@/assets/metric-card-status-icon.png'
import metricCardBudgetIcon from '@/assets/metric-card-budget-icon.png'
import metricCardResponsesIcon from '@/assets/metric-card-responses-icon.png'

function formatMoneyUsd(n: number): string {
  if (!Number.isFinite(n)) return '$0'
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })
}

/** Figma 4383:3896 / 4383:3905 / 4383:3913 — label row */
const labelSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
} as const

/** Figma — value line 30px / 28px line-height */
const valueSx = {
  fontFamily: '"Inter", sans-serif',
  fontWeight: 700,
  fontSize: '30px',
  lineHeight: '28px',
  letterSpacing: '-0.4492px',
} as const

const CARD_SX = {
  borderRadius: '8px',
  p: '20px',
  minHeight: 137,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  alignItems: 'flex-start',
  boxSizing: 'border-box',
} as const

interface Props {
  budgetUsed: number
  totalResponses: number
  /** When true, status value text shows Completed instead of Active. */
  campaignEnded?: boolean
}

export function LaunchMetricCards({
  budgetUsed,
  totalResponses,
  campaignEnded = false,
}: Readonly<Props>) {
  const statusLabel = campaignEnded ? 'Completed' : 'Active'

  return (
    <Grid container spacing={2} sx={{ mb: '32px' }}>
      <Grid item xs={12} sm={4}>
        <Box
          data-testid="launch-metric-status"
          sx={{
            ...CARD_SX,
            bgcolor: '#EAF3DE',
          }}
        >
          <Box
            component="img"
            src={metricCardStatusIcon}
            alt=""
            aria-hidden
            sx={{
              width: 42,
              height: 42,
              objectFit: 'cover',
              display: 'block',
              flexShrink: 0,
            }}
          />
          <Typography sx={{ ...labelSx, color: '#0D9467' }}>Status</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Box
              component="span"
              sx={{
                width: 10,
                height: 10,
                borderRadius: '9999px',
                bgcolor: '#1AA24C',
                flexShrink: 0,
              }}
              aria-hidden
            />
            <Typography sx={{ ...valueSx, color: '#1AA24C' }}>{statusLabel}</Typography>
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Box
          data-testid="launch-metric-budget-used"
          sx={{
            ...CARD_SX,
            bgcolor: '#FAEFDA',
          }}
        >
          <Box
            component="img"
            src={metricCardBudgetIcon}
            alt=""
            aria-hidden
            sx={{
              width: 41,
              height: 40,
              objectFit: 'cover',
              display: 'block',
              flexShrink: 0,
            }}
          />
          <Typography sx={{ ...labelSx, color: '#633806' }}>
            Budget Used
          </Typography>
          <Typography sx={{ ...valueSx, color: '#633806' }}>
            {formatMoneyUsd(budgetUsed)}
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Box
          data-testid="launch-metric-responses"
          sx={{
            ...CARD_SX,
            bgcolor: '#E6F1FB',
          }}
        >
          <Box
            component="img"
            src={metricCardResponsesIcon}
            alt=""
            aria-hidden
            sx={{
              width: 42,
              height: 42,
              objectFit: 'cover',
              display: 'block',
              flexShrink: 0,
            }}
          />
          <Typography sx={{ ...labelSx, color: '#0B447C' }}>Responses</Typography>
          <Typography sx={{ ...valueSx, color: '#0B447C' }}>
            {totalResponses.toLocaleString()}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  )
}
